import useUpdateAddressPropertyInfo from '@/api/mutations/addresses/useUpdateAddressPropertyInfo';
import projectKeys from '@/api/queries/project/keyFactory';
import { solarDesignerStateKey } from '@/api/queries/project/useSolarDesignerState';
import { OccupancyArchetype } from '@/gql/graphql';
import {
  Alert,
  Field,
  Form,
  H4,
  InputGroup,
  NumberInput,
  Select,
  Text,
} from '@payaca/ambrosia';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { OCCUPANCY_ARCHETYPE_OPTIONS } from '../../projectPage/components/propertyInfo/propertyInfoOptions';

export type HouseholdElectricity = {
  electricityOccupancyArchetype?: OccupancyArchetype | null;
  annualElectricityConsumptionKwh?: number | null;
};

type HouseholdElectricitySectionProps = {
  projectId: string;
  addressId?: string;
  household?: HouseholdElectricity | null;
  showOccupancy: boolean;
  onHouseholdChange: (household: HouseholdElectricity) => void;
};

/**
 * Annual use (every region) and weekday occupancy (UK) on the Glow
 * performance task. Same address columns as the property panel; empty is allowed.
 */
const HouseholdElectricitySection: FC<HouseholdElectricitySectionProps> = (
  props
) => {
  const { projectId, addressId, household, showOccupancy, onHouseholdChange } =
    props;
  const queryClient = useQueryClient();
  const { mutate, isPending } = useUpdateAddressPropertyInfo({
    meta: { skipGlobalErrorToast: true },
  });
  const dirty = useRef(false);
  /**
   * What the server holds. `household` cannot stand in for it: `emit` lifts
   * every keystroke to the parent, which passes it straight back down.
   */
  const savedConsumptionKwh = useRef<number | null>(
    household?.annualElectricityConsumptionKwh ?? null
  );
  const [occupancy, setOccupancy] = useState<OccupancyArchetype | undefined>(
    household?.electricityOccupancyArchetype ?? undefined
  );
  const [consumptionKwh, setConsumptionKwh] = useState<number | null>(
    household?.annualElectricityConsumptionKwh ?? null
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (dirty.current) return;
    setOccupancy(household?.electricityOccupancyArchetype ?? undefined);
    setConsumptionKwh(household?.annualElectricityConsumptionKwh ?? null);
    savedConsumptionKwh.current =
      household?.annualElectricityConsumptionKwh ?? null;
  }, [
    household?.electricityOccupancyArchetype,
    household?.annualElectricityConsumptionKwh,
  ]);

  const methods = useForm({
    values: {
      electricityOccupancyArchetype: occupancy,
      annualElectricityConsumptionKwh: consumptionKwh,
    },
  });

  const persist = (patch: {
    electricityOccupancyArchetype?: OccupancyArchetype | null;
    annualElectricityConsumptionKwh?: number | null;
  }) => {
    if (!addressId) return;
    setErrorMessage(null);
    mutate(
      { addressId, ...patch },
      {
        onSuccess: () => {
          dirty.current = false;
          if (patch.annualElectricityConsumptionKwh !== undefined) {
            savedConsumptionKwh.current = patch.annualElectricityConsumptionKwh;
          }
          void queryClient.invalidateQueries({
            queryKey: solarDesignerStateKey(projectId),
          });
          void queryClient.invalidateQueries({
            queryKey: projectKeys.overview(projectId),
          });
        },
        onError: () => {
          setErrorMessage('Could not save electricity details. Try again.');
        },
      }
    );
  };

  const emit = (next: {
    occupancy?: OccupancyArchetype;
    consumption: number | null;
  }) => {
    dirty.current = true;
    onHouseholdChange({
      electricityOccupancyArchetype: next.occupancy ?? null,
      annualElectricityConsumptionKwh: next.consumption,
    });
  };

  return (
    <Form
      methods={methods}
      onSubmit={() => undefined}
      className="space-y-3"
      aria-label="Electricity"
    >
      <H4>Electricity</H4>
      {showOccupancy && (
        <Field name="electricityOccupancyArchetype">
          <Field.Label>Weekday occupancy</Field.Label>
          <Select
            multiple={false}
            options={[...OCCUPANCY_ARCHETYPE_OPTIONS]}
            placeholder="Select occupancy"
            value={occupancy}
            disabled={!addressId || isPending}
            onChange={(value) => {
              const next = value as OccupancyArchetype | undefined;
              setOccupancy(next);
              emit({ occupancy: next, consumption: consumptionKwh });
              persist({ electricityOccupancyArchetype: next ?? null });
            }}
          />
        </Field>
      )}
      <Field
        name="annualElectricityConsumptionKwh"
        className="flex items-center justify-between gap-3 space-y-0"
      >
        <Field.Label>Annual use</Field.Label>
        <InputGroup className="w-52">
          <NumberInput
            as={InputGroup.RawInput}
            min={0}
            step={1}
            placeholder="Typical 4,500"
            value={consumptionKwh}
            disabled={!addressId || isPending}
            onChange={(value) => {
              setConsumptionKwh(value);
              emit({ occupancy, consumption: value });
            }}
            onBlur={() => {
              if (consumptionKwh === savedConsumptionKwh.current) return;
              persist({ annualElectricityConsumptionKwh: consumptionKwh });
            }}
          />
          <InputGroup.Text>kWh</InputGroup.Text>
        </InputGroup>
      </Field>
      {errorMessage && (
        <Alert colour="red">
          <Text level="supporting">{errorMessage}</Text>
        </Alert>
      )}
    </Form>
  );
};

export default HouseholdElectricitySection;
